import { useEffect, useRef } from "react";
import { motion } from "framer-motion";

let audioCtx = null;

const getCtx = () => {
  if (!audioCtx) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (AC) audioCtx = new AC();
  }
  return audioCtx;
};

const impulse = (ctx, at, vol) => {
  const dur = 0.022;
  const t = ctx.currentTime + at;
  const buffer = ctx.createBuffer(1, Math.floor(ctx.sampleRate * dur), ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i++) {
    data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / data.length, 2.4);
  }
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  const filter = ctx.createBiquadFilter();
  filter.type = "bandpass";
  filter.frequency.value = 2400;
  filter.Q.value = 1.6;
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(vol, t);
  gain.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  src.connect(filter).connect(gain).connect(ctx.destination);
  src.start(t);
};

export const playTick = () => {
  try {
    const ctx = getCtx();
    if (!ctx || ctx.state !== "running") return;
    impulse(ctx, 0.05, 0.1);
    impulse(ctx, 0.15, 0.12);
  } catch {
    /* audio unavailable — navigation works silently */
  }
};

const LINKS = [
  { label: "What We Do", href: "#what-we-do", id: "what-we-do" },
  { label: "Portfolio", href: "#studio", id: "work" },
  { label: "Contact", href: "#contact", id: "contact" },
];

export const scrollTo = (e, href) => {
  e.preventDefault();
  const lenis = window.__lenis;
  if (lenis) lenis.scrollTo(href, { duration: 1.4 });
  else document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
};

export default function Nav({ onOpenConcierge }) {
  const rowRef = useRef(null);
  const tagRef = useRef(null);

  useEffect(() => {
    const fit = () => {
      const row = rowRef.current;
      const tag = tagRef.current;
      if (!row || !tag) return;
      tag.style.letterSpacing = "0px";
      const rowW = row.getBoundingClientRect().width;
      const textW = tag.getBoundingClientRect().width;
      const gap = Math.max((rowW - 2 - textW) / 28, 0.4);
      tag.style.letterSpacing = `${gap}px`;
    };
    fit();
    const ro = new ResizeObserver(fit);
    if (rowRef.current) ro.observe(rowRef.current);
    window.addEventListener("resize", fit);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(fit);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", fit);
    };
  }, []);

  useEffect(() => {
    const unlock = () => {
      const ctx = getCtx();
      if (ctx && ctx.state === "suspended") ctx.resume();
    };
    window.addEventListener("pointerdown", unlock);
    window.addEventListener("keydown", unlock);
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, []);

  return (
    <motion.header
      initial={{ y: -32, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
      className="fixed inset-x-0 top-0 z-50 mix-blend-difference"
      data-testid="site-nav"
    >
      <nav className="flex items-center justify-between px-6 py-5 sm:px-10 lg:px-14">
        <a
          href="#top"
          onClick={(e) => scrollTo(e, "#top")}
          data-testid="nav-logo"
          className="flex flex-col items-start gap-1.5"
        >
          <span ref={rowRef} className="flex items-center gap-3">
            <img src="/assets/logo-duo.png" alt="Gentle&Monster logo" className="h-7 w-auto" />
            <span className="whitespace-nowrap font-display text-[9px] font-bold uppercase tracking-tight text-white sm:text-[11px] lg:text-sm">
              Gentle&nbsp;&amp;&nbsp;Monster
            </span>
          </span>
          <span
            ref={tagRef}
            data-testid="nav-descriptor"
            className="ml-[2px] whitespace-nowrap font-monoalt text-[9px] font-normal uppercase text-white/50"
          >
            Meta Ads &amp; Creative Strategy
          </span>
        </a>
        <div className="hidden items-center gap-10 font-monoalt text-[11px] uppercase tracking-[0.25em] lg:flex">
          {LINKS.map((l) => (
            <a
              key={l.id}
              href={l.href}
              onClick={(e) => scrollTo(e, l.href)}
              onPointerEnter={playTick}
              data-testid={`nav-link-${l.id}`}
              className="nav-tick inline-flex items-baseline gap-1.5 text-white/60"
            >
              <span aria-hidden="true">+</span>
              {l.label}
            </a>
          ))}
        </div>
        <button
          type="button"
          onClick={onOpenConcierge}
          onPointerEnter={playTick}
          data-testid="nav-cta"
          className="nav-cta shrink-0 whitespace-nowrap font-monoalt text-[11px] uppercase tracking-[0.25em] text-white"
        >
          <span aria-hidden="true">[ </span>
          <span className="nav-cta-blink">Let&apos;s Talk</span>
          <span aria-hidden="true"> ]</span>
        </button>
      </nav>
    </motion.header>
  );
}
